import { Role } from "@/enums/role";
import type { IExternalUser, IUser, OtherUser } from "@/interfaces/user";
import AuthProvider from "@/providers/auth.provider";
import { Provider } from "@/providers/http.provider";
import type { User } from "firebase/auth";
import type { AppSliceI } from "../slices/app.slice";
import { setAppStateAction } from "../slices/app.slice";
import type { AppDispatch } from "../store";
import { userActions } from "./user.actions";

const INTERNAL_ORG_OVERRIDE_KEY = "internalOrgOverride";

const logout = () => {
  return (dispatch: AppDispatch) => {
    dispatch(
      setAppStateAction({
        user: undefined,
        loading: false,
        token: undefined,
        redirectTo: undefined,
      }),
    );

    // Clear organization override on logout
    sessionStorage.removeItem(INTERNAL_ORG_OVERRIDE_KEY);

    Provider.setUid("");
    Provider.setToken("");
    Provider.setLassoToken("");
  };
};

// Helper function to set organization override for INTERNAL users
export const setInternalOrgOverride = (organizationId: string, organizationName: string) => {
  sessionStorage.setItem(INTERNAL_ORG_OVERRIDE_KEY, JSON.stringify({ organizationId, organizationName }));
};

// Helper function to get the current organization override (if any)
export const getInternalOrgOverride = (): { organizationId: string; organizationName: string } | null => {
  try {
    const stored = sessionStorage.getItem(INTERNAL_ORG_OVERRIDE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.organizationId && parsed?.organizationName) return parsed;
  } catch {
    // ignore
  }
  return null;
};

const setUser = (user?: IUser) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      setAppStateAction({
        user,
      }),
    );
  };
};

const setAppState = (data: AppSliceI) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      setAppStateAction({
        ...data,
      }),
    );
  };
};

const getUserDetails = (user: User, firebaseToken: string, onSuccess?: () => void) => {
  return async (dispatch: AppDispatch) => {
    Provider.setToken(firebaseToken);
    Provider.setCookies(user.uid, firebaseToken);
    Provider.setUid(user.uid);

    const userDetail = await AuthProvider.getUserDetails();
    if (!userDetail.success) {
      console.error("getUserDetails failed:", userDetail);
      throw new Error("getUserDetails failed");
    }

    const data = userDetail.data;

    const newlyRegistered = await registerUserIfNeeded(user);
    if (newlyRegistered) {
      // re-get the token because claims are set on register
      firebaseToken = await user.getIdToken(true);
      Provider.setToken(firebaseToken);
      Provider.setCookies(user.uid, firebaseToken);
    }

    // If customSettings.offices exists and has items, use it for officeList
    if (data.extraData?.customSettings?.offices && data.extraData.customSettings.offices.length > 0) {
      data.extraData.officeList = data.extraData.customSettings.offices;
    }

    // Apply organization override for INTERNAL users
    if (data.role === Role.INTERNAL) {
      const orgOverride = sessionStorage.getItem(INTERNAL_ORG_OVERRIDE_KEY);
      if (orgOverride) {
        try {
          const { organizationId, organizationName } = JSON.parse(orgOverride);
          if (organizationId && organizationName) {
            data.organizationId = organizationId;
            data.organizationName = organizationName;
          }
        } catch {
          // Invalid stored data, clear it
          sessionStorage.removeItem(INTERNAL_ORG_OVERRIDE_KEY);
        }
      }
    }

    dispatch(appActions.setUser(data));

    if (userDetail?.lassoToken) localStorage.setItem("lassoToken", userDetail?.lassoToken);

    dispatch(appActions.setAppState({ loading: false, lassoToken: userDetail?.lassoToken }));
    dispatch(
      userActions.setUserState({
        users: data.extraData?.otherUserList as unknown as IExternalUser[],
      }),
    );

    dispatch(
      setAppStateAction({
        loading: false,
        user: userDetail.data,
      }),
    );
    onSuccess?.();
  };
};

const registerUserIfNeeded = async (user: User) => {
  const tokenResult = await user.getIdTokenResult(true);
  const isRegistered = tokenResult.claims?.allowedUser === true;
  if (isRegistered) return false;
  const { success, error } = await AuthProvider.registerNewUser(userWithJustEmail(user.email!));
  if (!success && error) console.error(error);

  return true;
};

const userWithJustEmail = (email: string): OtherUser => {
  return {
    email,
    firstName: "",
    lastName: "",
    photoURL: "",
    role: "",
    lassoUserId: "",
  };
};

export const appActions = {
  logout,
  setUser,
  setAppState,
  getUserDetails,
};
